import { createAxiosInstance } from "./auth";
import { MARKETPLACE_BASE_URL } from "./configApi";

const MARKETPLACE_API = createAxiosInstance(MARKETPLACE_BASE_URL);

// ========================
// CORE / DASHBOARD
// ========================
export const getDashboardStats = () =>
  MARKETPLACE_API.get("core/dashboard/stats/");

export const getCapabilities = () =>
  MARKETPLACE_API.get("core/me/capabilities/");

export const getOrderTracking = () =>
  MARKETPLACE_API.get("core/orders/tracking/");

// ========================
// NOTIFICATIONS
// ========================
export const getNotifications = (params) =>
  MARKETPLACE_API.get("core/notifications/", { params });

export const getNotification = (id) =>
  MARKETPLACE_API.get(`core/notifications/${id}/`);

export const markNotificationRead = (id) =>
  MARKETPLACE_API.post(`core/notifications/${id}/mark-read/`);

export const markAllNotificationsRead = () =>
  MARKETPLACE_API.post("core/notifications/mark-all-read/");

export const getNotificationsSummary = () =>
  MARKETPLACE_API.get("core/notifications/summary/");

// ========================
// AUDIT LOGS
// ========================
export const getAuditLogs = (params) =>
  MARKETPLACE_API.get("core/audit-logs/", { params });

export const getAuditLog = (id) =>
  MARKETPLACE_API.get(`core/audit-logs/${id}/`);

// ========================
// OFFERS
// ========================
export const getOffers = (params) =>
  MARKETPLACE_API.get("offers/", { params });

export const getOffer = (id) =>
  MARKETPLACE_API.get(`offers/${id}/`);

export const createOffer = (data) =>
  MARKETPLACE_API.post("offers/", data);

export const updateOffer = (id, data) =>
  MARKETPLACE_API.patch(`offers/${id}/`, data);

export const deleteOffer = (id) =>
  MARKETPLACE_API.delete(`offers/${id}/`);

export const getMyOffers = (params) =>
  MARKETPLACE_API.get("offers/mine/", { params });

export const getOffersStats = () =>
  MARKETPLACE_API.get("offers/stats/");

export const getMarketplaceOffers = (params) =>
  MARKETPLACE_API.get("offers/marketplace/", { params });

export const getPublicOffers = (params) =>
  MARKETPLACE_API.get("offers/public-marketplace/", { params });

export const checkOfferDelete = (id) =>
  MARKETPLACE_API.get(`offers/${id}/check-delete/`);

// ========================
// COLLABORATIONS
// ========================
export const getCollaborations = (params) =>
  MARKETPLACE_API.get("collaborations/collaborations/", { params });

export const getCollaboration = (id) =>
  MARKETPLACE_API.get(`collaborations/collaborations/${id}/`);

// Règles d'affectation actives du store + jeton signé (assignment_token)
// exigé par integration_service pour affecter une commande à la main.
export const getMySourceConnections = () =>
  MARKETPLACE_API.get("collaborations/collaborations/my-source-connections/");

// Boutiques / produits déjà confiés (actifs) ou demandés (en attente) :
// sert à griser les choix interdits avant d'envoyer une demande.
export const getAssignmentCoverage = () =>
  MARKETPLACE_API.get("collaborations/collaborations/assignment-coverage/");

export const createCollaboration = (data) =>
  MARKETPLACE_API.post("collaborations/collaborations/", data);

export const updateCollaboration = (id, data) =>
  MARKETPLACE_API.patch(`collaborations/collaborations/${id}/`, data);

export const deleteCollaboration = (id) =>
  MARKETPLACE_API.delete(`collaborations/collaborations/${id}/`);

export const respondToCollaboration = (id, data) =>
  MARKETPLACE_API.post(`collaborations/collaborations/${id}/respond/`, data);

export const deactivateCollaboration = (id) =>
  MARKETPLACE_API.post(`collaborations/collaborations/${id}/deactivate/`);

export const activateCollaboration = (id) =>
  MARKETPLACE_API.post(`collaborations/collaborations/${id}/activate/`);

export const getCollaborationsSummary = () =>
  MARKETPLACE_API.get("collaborations/collaborations/summary/");

export const getCollaborationThread = (id) =>
  MARKETPLACE_API.get(`collaborations/collaborations/${id}/thread/`);

export const expirePendingCollaborations = () =>
  MARKETPLACE_API.post("collaborations/collaborations/expire-pending/");

export const reviewCollaboration = (id, data) =>
  MARKETPLACE_API.post(`collaborations/collaborations/${id}/review/`, data);

export const updateCollaborationSLA = (id, data) =>
  MARKETPLACE_API.patch(`collaborations/collaborations/${id}/sla/`, data);

// ========================
// WALLETS
// ========================
export const getWallets = (params) =>
  MARKETPLACE_API.get("wallets/wallets/", { params });

export const getWallet = (id) =>
  MARKETPLACE_API.get(`wallets/wallets/${id}/`);

export const createWallet = (data) =>
  MARKETPLACE_API.post("wallets/wallets/", data);

export const updateWallet = (id, data) =>
  MARKETPLACE_API.patch(`wallets/wallets/${id}/`, data);

export const getWalletSummary = () =>
  MARKETPLACE_API.get("wallets/wallets/summary/");

export const addBalanceToWallet = (id, data) =>
  MARKETPLACE_API.post(`wallets/wallets/${id}/add-balance/`, data);

export const getWalletTransfers = (params) =>
  MARKETPLACE_API.get("wallets/wallet-transfers/", { params });

export const getWalletTransfer = (id) =>
  MARKETPLACE_API.get(`wallets/wallet-transfers/${id}/`);

export const createWalletTransfer = (data) =>
  MARKETPLACE_API.post("wallets/wallet-transfers/", data);

export const getMyTransactions = (params) =>
  MARKETPLACE_API.get("wallets/wallet-transfers/my-transactions/", { params });

export const getTransactionStats = () =>
  MARKETPLACE_API.get("wallets/wallet-transfers/stats/");

// ========================
// COMMISSIONS
// ========================
export const getCommissions = (params) =>
  MARKETPLACE_API.get("commissions/commissions/", { params });

export const getCommission = (id) =>
  MARKETPLACE_API.get(`commissions/commissions/${id}/`);

export const getCommissionStats = () =>
  MARKETPLACE_API.get("commissions/commissions/stats/");

export const getPaymentAttempts = (id) =>
  MARKETPLACE_API.get(`commissions/commissions/${id}/payment-attempts/`);

export const getWalletTransactions = (params) =>
  MARKETPLACE_API.get("commissions/wallet-transactions/", { params });

export const getWalletTransaction = (id) =>
  MARKETPLACE_API.get(`commissions/wallet-transactions/${id}/`);

export const getWebhookEvents = (params) =>
  MARKETPLACE_API.get("commissions/webhook-events/", { params });

export const getWebhookEvent = (id) =>
  MARKETPLACE_API.get(`commissions/webhook-events/${id}/`);

export const createCommissionWebhook = (data) =>
  MARKETPLACE_API.post("commissions/webhooks/commission/", data);

// ========================
// INVOICES
// ========================
export const getInvoices = (params) =>
  MARKETPLACE_API.get("invoices/invoices/", { params });

export const getInvoice = (id) =>
  MARKETPLACE_API.get(`invoices/invoices/${id}/`);

export const payCommission = (data) =>
  MARKETPLACE_API.post("invoices/invoices/pay-commission/", data);

export const getInvoiceStats = () =>
  MARKETPLACE_API.get("invoices/invoices/my-stats/");

export const downloadInvoicePDF = (id) =>
  MARKETPLACE_API.get(`invoices/invoices/${id}/download-pdf/`, {
    responseType: "blob",
  });

export const exportInvoices = (params) =>
  MARKETPLACE_API.get("invoices/invoices/export/", {
    params,
    responseType: "blob",
  });

export const getInvoiceTransferDetails = (id) =>
  MARKETPLACE_API.get(`invoices/invoices/${id}/transfer-details/`);

export const getInvoicesByOrder = (orderId) =>
  MARKETPLACE_API.get(`invoices/invoices/by-order/${orderId}/`);

// ========================
// MESSAGING
// ========================
export const getConversations = (params) =>
  MARKETPLACE_API.get("messaging/conversations/", { params });

export const getConversation = (id) =>
  MARKETPLACE_API.get(`messaging/conversations/${id}/`);

export const createConversation = (data) =>
  MARKETPLACE_API.post("messaging/conversations/", data);

export const updateConversation = (id, data) =>
  MARKETPLACE_API.patch(`messaging/conversations/${id}/`, data);

export const deleteConversation = (id) =>
  MARKETPLACE_API.delete(`messaging/conversations/${id}/`);

export const getConversationMessages = (id, params) =>
  MARKETPLACE_API.get(`messaging/conversations/${id}/messages/`, { params });

export const sendMessage = (id, data) =>
  MARKETPLACE_API.post(`messaging/conversations/${id}/send-message/`, data);

export const injectCollaboration = (id, data) =>
  MARKETPLACE_API.post(`messaging/conversations/${id}/inject-collaboration/`, data);

export const markConversationRead = (id) =>
  MARKETPLACE_API.post(`messaging/conversations/${id}/mark-read/`);

export const archiveConversation = (id) =>
  MARKETPLACE_API.post(`messaging/conversations/${id}/archive/`);

export const reopenConversation = (id) =>
  MARKETPLACE_API.post(`messaging/conversations/${id}/reopen/`);

export const getConversationStats = () =>
  MARKETPLACE_API.get("messaging/conversations/stats/");

export const getUnreadConversations = () =>
  MARKETPLACE_API.get("messaging/conversations/unread/");

export const getMessages = (params) =>
  MARKETPLACE_API.get("messaging/messages/", { params });

export const getMessage = (id) =>
  MARKETPLACE_API.get(`messaging/messages/${id}/`);

export default MARKETPLACE_API;
